import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import PayslipStateBadge from './PayslipStateBadge';
import PayslipModal from './PayslipModal';
import TableList from '../../../components/Tools/TableList';

const renderMonthCell = (row, monthKey, onBadgeClick) => {
    const payslipInfo = row.monthly_states[monthKey];
    if (!payslipInfo) {
        return <span className="text-gray-400 text-xs">-</span>;
    }
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onBadgeClick(row, monthKey);
            }}
            className="w-full h-full flex justify-center items-center"
        >
            <PayslipStateBadge state={payslipInfo.state} />
        </button>
    );
};

const buildMonthColumn = (month, index, onBadgeClick) => ({
    key: `month_${index + 1}`,
    label: month,
    sortable: false,
    render: (row) => renderMonthCell(row, String(index + 1), onBadgeClick)
});

const AnnualPayslipView = ({ year }) => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    
    // État pour la pagination et la recherche
    const [pagination, setPagination] = useState({
        total_count: 0,
        offset: 0,
        limit: 10,
        total_pages: 0,
        page: 1
    });
    
    const [searchQuery, setSearchQuery] = useState('');
    const [filterBy, setFilterBy] = useState({ filters: [] });

    const months = t('annualPayslipView.monthAbbreviations', { returnObjects: true });

    // Préparer les colonnes pour TableList
    const columns = useMemo(() => [
        {
            key: 'employee_name',
            label: t('annualPayslipView.employee'),
            sortable: true,
            avatar: true
        },
        ...months.map((month, index) => buildMonthColumn(month, index, (row, monthKey) => handleBadgeClick(row, monthKey)))
    ], [months, t]);

    const fetchAnnualData = useCallback(async (page = 1, search = '', filters = {}) => {
        if (!year) return;
        
        setLoading(true);
        
        const offset = (page - 1) * pagination.limit;
        
        try {
            const response = await apiService.getElement(
                `/payslip/annual_overview/${year}?offset=${offset}&limit=${pagination.limit}&search=${search}`
            );
            
            setData(response.data.overview || []);
            
            // Mettre à jour l'état de pagination avec les données du backend
            if (response.data.pagination) {
                setPagination({
                    ...response.data.pagination,
                    page
                });
            }
        } catch (error) {
            console.error("Error fetching annual overview:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [year, pagination.limit]);

    useEffect(() => {
        fetchAnnualData(1);
    }, [fetchAnnualData]);

    const handleBadgeClick = (employee, month) => {
        setSelectedEmployee(employee);
        setSelectedMonth(month);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEmployee(null);
        setSelectedMonth(null);
    };

    const handleUpdate = () => {
        // Rafraîchir les données après une action dans le modal
        fetchAnnualData(pagination.page, searchQuery, filterBy);
    };

    const handlePageChange = (page, search, filters) => {
        fetchAnnualData(page, search, filters);
    };

    return (
        <div className="">
            {/* <h2 className="text-xl font-bold text-gray-800 mb-4 flex justify-center w-full">Vue Annuelle des Bulletins - {year}</h2> */}
            <TableList
                columns={columns}
                data={data}
                searchDisplay={true}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filterBy={filterBy}
                setFilterBy={setFilterBy}
                showFilter={true}
                pagination={{
                    page: pagination.page,
                    total_pages: pagination.total_pages,
                    total_count: pagination.total_count
                }}
                onPageChange={handlePageChange}
                loading={loading}
                model="hr.employee" // Spécifier le modèle pour les filtres avancés
            />
            
            {selectedEmployee && (
                <PayslipModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    employee={selectedEmployee}
                    month={selectedMonth}
                    year={year}
                    onUpdate={handleUpdate}
                />
            )}
        </div>
    );
};

export default AnnualPayslipView;